import type { CompressedTexture, WebGLRenderer } from 'three'
import type { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js'
import type { WebGPURenderer } from 'three/webgpu'
import { MeshLoadCancelledError } from '@/plugins/mesh/errors'
import type { MeshKtx2Options } from '@/plugins/mesh/types'

/** 请求只共享设备和转码能力，不共享 URL、认证或取消状态。 */
export interface IMeshKtx2Service {
  readonly maxTextureSize: number
  transcode(data: ArrayBuffer): Promise<CompressedTexture>
}

class MeshKtx2WorkerError extends Error {}

/** 规范化部署配置，拒绝空目录和非有限 Worker 数量。 */
export function resolveMeshKtx2Options(options: MeshKtx2Options): Required<MeshKtx2Options> {
  const path = options.transcoderPath?.trim()
  if (!path) throw new RangeError('KTX2 transcoderPath 必须是非空目录')
  const limit = options.workerLimit ?? 2
  if (!Number.isFinite(limit)) throw new RangeError('KTX2 workerLimit 必须是有限数值')
  return {
    transcoderPath: path.endsWith('/') ? path : `${path}/`,
    workerLimit: Math.max(1, Math.min(16, Math.floor(limit))),
  }
}

/**
 * KTX2 需要的 renderer 能力并集。
 *
 * WebGL2 走 `WebGLRenderer`（上限读 `capabilities.maxTextureSize`）；WebGPU 走 engine 内部的
 * `WebGPURenderer`（r186 `KTX2Loader.detectSupport()` 原生支持，用 `hasFeature()` 探测压缩格式），
 * 其上传上限必须由调用方从只读 graphics info 传入 `deviceLimits.maxTextureDimension2D`，
 * 因为 WebGPU renderer 不暴露 `capabilities`。
 */
export type MeshKtx2Renderer = WebGLRenderer | WebGPURenderer

/** 将宿主已 detectSupport/init 的实例包装为借用能力；不调用其 dispose。 */
export function borrowMeshKtx2Loader(loader: KTX2Loader, maxTextureSize: number): IMeshKtx2Service {
  return {
    maxTextureSize,
    transcode: (data) => transcodeMeshKtx2(loader, data),
  }
}

async function transcodeMeshKtx2(
  loader: KTX2Loader,
  data: ArrayBuffer,
  assertActive?: () => void
): Promise<CompressedTexture> {
  assertActive?.()
  await loader.init()
  assertActive?.()
  return new Promise((resolve, reject) => {
    let settled = false
    const workers: Worker[] = []
    const cleanup = () => {
      settled = true
      for (const worker of workers) {
        worker.removeEventListener('error', failWorker)
        worker.removeEventListener('messageerror', failWorker)
      }
    }
    const fail = (error: unknown) => {
      cleanup()
      reject(error)
    }
    const failWorker = (event: Event) =>
      fail(
        new MeshKtx2WorkerError(
          `KTX2 Worker 初始化/转码失败: ${(event as ErrorEvent).message ?? event.type}`
        )
      )
    try {
      // 专属输入避免 Three.js 按 ArrayBuffer 缓存时跨请求返回同一 Texture。
      loader.parse(
        data.slice(0),
        (texture) => {
          if (settled) texture.dispose()
          else {
            cleanup()
            resolve(texture)
          }
        },
        fail
      )
      // Three.js 在已完成 init 的下一微任务中创建 Worker；监听致命错误，
      // 避免其 WorkerPool 只监听 message 导致 Promise 永久挂起。
      queueMicrotask(() => {
        if (settled) return
        for (const worker of loader.workerPool.workers) {
          workers.push(worker)
          worker.addEventListener('error', failWorker)
          worker.addEventListener('messageerror', failWorker)
        }
      })
    } catch (error) {
      fail(error)
    }
  })
}

/** 插件拥有的懒加载转码器；多个请求共享初始化和一个 Worker 池。 */
export class MeshKtx2Service implements IMeshKtx2Service {
  private readonly _options: Required<MeshKtx2Options>
  private _renderer?: MeshKtx2Renderer
  /** WebGPU 等无 `capabilities` 的 renderer 必须显式提供上传上限。 */
  private _maxTextureSize?: number
  private _loader?: KTX2Loader
  private _initializing?: Promise<KTX2Loader>
  private _disposed = false
  private _initialized = false
  private _failure?: Error
  private readonly _pending = new Set<(error: unknown) => void>()

  constructor(options: MeshKtx2Options) {
    this._options = resolveMeshKtx2Options(options)
  }

  /**
   * 上传前的纹理尺寸上限。
   *
   * 优先用调用方显式传入的值（WebGPU 必需）；否则读 WebGL 的 `capabilities.maxTextureSize`；
   * 都拿不到时返回 `NaN`——上层据此拒绝加载，而不是用 `Infinity` 绕过上传边界。
   */
  get maxTextureSize(): number {
    if (this._maxTextureSize !== undefined) return this._maxTextureSize
    const capabilities = (this._renderer as WebGLRenderer | undefined)?.capabilities
    const fromCapabilities = capabilities?.maxTextureSize
    return typeof fromCapabilities === 'number' && Number.isFinite(fromCapabilities)
      ? fromCapabilities
      : Number.NaN
  }

  /**
   * renderer 在插件初始化后才可用，不触发 Basis 下载。
   *
   * @param renderer - WebGL 或 WebGPU renderer（WebGPU 走 engine 内部边界）
   * @param maxTextureSize - WebGPU 必须显式传入的上传上限；WebGL 省略时读 capabilities
   */
  setRenderer(renderer: MeshKtx2Renderer, maxTextureSize?: number): void {
    this._renderer = renderer
    this._maxTextureSize =
      maxTextureSize !== undefined && Number.isFinite(maxTextureSize) && maxTextureSize >= 1
        ? Math.floor(maxTextureSize)
        : undefined
  }

  private getLoader(): Promise<KTX2Loader> {
    if (this._disposed)
      return Promise.reject(this._failure ?? new MeshLoadCancelledError('(KTX2 服务已销毁)'))
    this._initializing ??= (async () => {
      if (!this._renderer) throw new Error('KTX2 需要先初始化 MeshPlugin renderer')
      const { KTX2Loader } = await import('three/addons/loaders/KTX2Loader.js')
      if (this._disposed) throw new MeshLoadCancelledError('(KTX2 服务已销毁)')
      const loader = new KTX2Loader()
        .setTranscoderPath(this._options.transcoderPath)
        .setWorkerLimit(this._options.workerLimit)
        .detectSupport(this._renderer)
      this._loader = loader
      // parse 内部还会在 init().then 中提交任务；保护实际提交点，避免
      // dispose 清空池后，迟到微任务重新创建 Worker。仅修改自有实例。
      const postMessage = loader.workerPool.postMessage.bind(loader.workerPool)
      loader.workerPool.postMessage = (message, transfer) => {
        this.assertActive()
        return postMessage(message, transfer)
      }
      try {
        await loader.init()
      } finally {
        this._initialized = true
        // init 不支持取消；迟到创建的 Worker URL 也必须释放。
        if (this._disposed) loader.dispose()
      }
      if (this._disposed) throw new MeshLoadCancelledError('(KTX2 服务已销毁)')
      return loader
    })()
    return this._initializing
  }

  transcode(data: ArrayBuffer): Promise<CompressedTexture> {
    return new Promise((resolve, reject) => {
      this._pending.add(reject)
      void this.getLoader()
        .then((loader) => transcodeMeshKtx2(loader, data, () => this.assertActive()))
        .then(
          (texture) => {
            if (this._disposed) texture.dispose()
            else resolve(texture)
          },
          (error: unknown) => {
            if (error instanceof MeshKtx2WorkerError) {
              this._failure = error
              this.close(error)
            }
            reject(error)
          }
        )
        .finally(() => this._pending.delete(reject))
    })
  }

  /** 先由插件取消请求，再终止自有 Worker；重复调用无副作用。 */
  dispose(): void {
    this.close(new MeshLoadCancelledError('(KTX2 服务已销毁)'))
  }

  private assertActive(): void {
    if (this._disposed) throw this._failure ?? new MeshLoadCancelledError('(KTX2 服务已销毁)')
  }

  private close(error: Error): void {
    if (this._disposed) return
    this._disposed = true
    for (const reject of this._pending) reject(error)
    this._pending.clear()
    if (this._initialized) this._loader?.dispose()
    this._renderer = undefined
    this._maxTextureSize = undefined
  }
}
